import { useEffect, useRef, useState } from "react";
import { Activity, Inbox, RefreshCcw, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { subscribe, forget, send } from "@/lib/deriv";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

const fmt = (n, dp = 2) =>
  n != null && Number.isFinite(+n)
    ? Number(n).toLocaleString(undefined, {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      })
    : "—";

const secondsLeft = (epochExpiry) => {
  if (!epochExpiry) return null;
  const s = Math.max(0, Math.floor(epochExpiry - Date.now() / 1000));
  if (s >= 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${s}s`;
};

// One row = one contract with a live subscription to proposal_open_contract.
const LiveContractRow = ({ contract, currency, onSettled, onLiveProfit }) => {
  const [live, setLive] = useState(null);
  const subRef = useRef(null);
  const [, force] = useState(0);
  // Feb 2026 v8.1 — Track whether the streaming sub has delivered yet so
  // the 3s polling fallback can quietly stop once data starts flowing.
  const lastFrameAtRef = useRef(0);

  // Trigger the seconds countdown every 1s
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sub = await subscribe(
          {
            proposal_open_contract: 1,
            contract_id: contract.contract_id,
          },
          (msg) => {
            if (cancelled) return;
            const c = msg?.proposal_open_contract;
            if (!c) return;
            lastFrameAtRef.current = Date.now();
            setLive(c);
            onLiveProfit?.(contract.contract_id, c.profit ?? 0, !!c.is_sold);
            if (c.is_sold) {
              onSettled?.(contract.contract_id, c);
            }
          }
        );
        subRef.current = sub.subscription_id;
      } catch {
        /* ignore — non-live contract */
      }
    })();
    return () => {
      cancelled = true;
      if (subRef.current) forget(subRef.current);
      subRef.current = null;
    };
  }, [contract.contract_id, onSettled, onLiveProfit]);

  // Feb 2026 v8.1 — POLLING FALLBACK for Live Trades dashes.
  // The per-row `proposal_open_contract` subscription is the primary path
  // but it silently fails on some Deriv accounts (multiplier contracts,
  // transient socket auth lag, OTP URL expiry). When that happens the row
  // sits at "—" forever because no `live` state ever lands.
  //
  // Belt-and-braces: every 3s send a ONE-SHOT `proposal_open_contract`
  // (no `subscribe`) and merge the snapshot into `live`. If the streaming
  // sub IS working we still poll quietly — costs ~1 request / 3s / row
  // (well under Deriv's per-account rate limit, and there are typically
  // 1-3 open contracts at a time).
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      // Skip if the stream delivered within the last 10s — sub is healthy.
      if (Date.now() - lastFrameAtRef.current < 10_000) return;
      try {
        const res = await send({
          proposal_open_contract: 1,
          contract_id: contract.contract_id,
        });
        if (cancelled) return;
        const c = res?.proposal_open_contract;
        if (!c) return;
        setLive(c);
        onLiveProfit?.(contract.contract_id, c.profit ?? 0, !!c.is_sold);
        if (c.is_sold) {
          onSettled?.(contract.contract_id, c);
        }
      } catch {
        /* silent — best-effort backfill */
      }
    };
    // First poll quickly (2s) so the row populates fast even if the
    // streaming sub hasn't connected yet, then every 3s thereafter.
    const initial = setTimeout(poll, 2_000);
    const id = setInterval(poll, 3_000);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(id);
    };
  }, [contract.contract_id, onSettled, onLiveProfit]);

  // Feb 2026 v7.10 — Fall back to backend-persisted live values from
  // bot_trades when the Deriv WS subscription isn't delivering (e.g.,
  // demo-mode sessions, expired tokens, transient WS errors). User
  // screenshot showed "ENTRY / CURRENT / SELL VALUE = —" because the
  // private-subscribe to proposal_open_contract was silently failing.
  // The reconciler refreshes these fields every 20s.
  const profit = live?.profit ?? contract?.current_profit ?? null;
  const profitPct =
    profit != null && contract.buy_price
      ? (profit / contract.buy_price) * 100
      : (contract?.current_profit_pct ?? null);
  const isProfit = profit != null && profit > 0;
  const isLoss = profit != null && profit < 0;

  const currentSpot = live?.current_spot ?? contract?.current_spot ?? null;
  const entrySpot = live?.entry_spot ?? contract?.entry_spot ?? null;
  const sellPrice = live?.bid_price ?? contract?.bid_price ?? null;
  const expiry = live?.date_expiry;
  const dateStart = live?.date_start ?? live?.purchase_time ?? null;
  const isSold = !!live?.is_sold;
  // Multiplier contracts have no expiry — Deriv sets date_expiry to ~year 2125
  // which renders as "52,560,798m 38s". Detect MULT* and show open-duration
  // (time since entry) instead, so the user sees a meaningful runtime.
  const ctypeStr = String(live?.contract_type || contract.contract_type || "").toUpperCase();
  const isMultiplier = ctypeStr.includes("MULT");
  const runSince = (() => {
    if (!dateStart) return null;
    const s = Math.max(0, Math.floor(Date.now() / 1000 - dateStart));
    if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    if (s >= 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${s}s`;
  })();
  const timeLeft = isMultiplier ? runSince : secondsLeft(expiry);
  const timeLabel = isMultiplier ? "open" : null;

  return (
    <div
      data-testid={`live-trade-${contract.contract_id}`}
      className={`p-4 border-b border-vts-line/50 transition ${
        isSold
          ? "opacity-60"
          : isProfit
          ? "bg-vts-profit/5"
          : isLoss
          ? "bg-vts-loss/5"
          : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold text-sm text-white">
              {live?.display_name || contract.symbol}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-mono ${
                live?.contract_type?.includes("CALL") || contract.contract_type === "CALL"
                  ? "border-vts-profit/40 text-vts-profit bg-vts-profit/10"
                  : live?.contract_type?.includes("PUT") || contract.contract_type === "PUT"
                  ? "border-vts-loss/40 text-vts-loss bg-vts-loss/10"
                  : "border-vts-blue/40 text-vts-blue bg-vts-blue/10"
              }`}
            >
              {live?.contract_type || contract.contract_type}
            </span>
            {isSold && (
              <span
                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono ${
                  isProfit
                    ? "bg-vts-profit/20 text-vts-profit border border-vts-profit/40"
                    : "bg-vts-loss/20 text-vts-loss border border-vts-loss/40"
                }`}
              >
                {isProfit ? "Won" : "Lost"}
              </span>
            )}
            {!isSold && timeLeft != null && (
              <span className="flex items-center gap-1 text-[10px] font-mono text-vts-silver/70 px-1.5 py-0.5 rounded border border-vts-line bg-vts-surface">
                <Clock className="h-2.5 w-2.5" /> {timeLeft}{timeLabel ? ` ${timeLabel}` : ""}
              </span>
            )}
          </div>
          <div className="text-[10px] font-mono text-vts-silver/50 mt-1 truncate">
            #{contract.contract_id} · Stake {fmt(contract.buy_price)} {currency} ·
            Payout {fmt(contract.payout)} {currency}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div
            data-testid={`live-trade-profit-${contract.contract_id}`}
            className={`font-mono text-lg tabular-nums font-medium ${
              isProfit
                ? "text-vts-profit"
                : isLoss
                ? "text-vts-loss"
                : "text-vts-silver/70"
            }`}
          >
            {profit != null ? (isProfit ? "+" : "") + fmt(profit) : "—"} {currency}
          </div>
          {profitPct != null && (
            <div
              className={`text-[10px] font-mono tabular-nums flex items-center gap-1 justify-end ${
                isProfit
                  ? "text-vts-profit"
                  : isLoss
                  ? "text-vts-loss"
                  : "text-vts-silver/50"
              }`}
            >
              {isProfit ? (
                <TrendingUp className="h-2.5 w-2.5" />
              ) : isLoss ? (
                <TrendingDown className="h-2.5 w-2.5" />
              ) : null}
              {profitPct.toFixed(2)}%
            </div>
          )}
        </div>
      </div>

      {/* Mini stats grid */}
      <div className="grid grid-cols-3 gap-2 text-[11px] mt-2">
        <div className="bg-vts-surface/60 border border-vts-line rounded p-1.5">
          <div className="text-[9px] uppercase tracking-wider text-vts-silver/50">
            Entry
          </div>
          <div className="font-mono text-vts-silver/90 tabular-nums">
            {fmt(entrySpot, entrySpot != null && entrySpot < 10 ? 4 : 2)}
          </div>
        </div>
        <div className="bg-vts-surface/60 border border-vts-line rounded p-1.5">
          <div className="text-[9px] uppercase tracking-wider text-vts-silver/50">
            Current
          </div>
          <div
            className={`font-mono tabular-nums ${
              currentSpot != null && entrySpot != null
                ? currentSpot > entrySpot
                  ? "text-vts-profit"
                  : currentSpot < entrySpot
                  ? "text-vts-loss"
                  : "text-white"
                : "text-white"
            }`}
          >
            {fmt(currentSpot, currentSpot != null && currentSpot < 10 ? 4 : 2)}
          </div>
        </div>
        <div className="bg-vts-surface/60 border border-vts-line rounded p-1.5">
          <div className="text-[9px] uppercase tracking-wider text-vts-silver/50">
            Sell value
          </div>
          <div className="font-mono text-white tabular-nums">
            {fmt(sellPrice)} {currency}
          </div>
        </div>
      </div>

      {/* Manual sell button — only when contract is sellable AND not yet sold */}
      {!isSold && live?.is_valid_to_sell && (
        <div className="mt-2 flex justify-end">
          <button
            data-testid={`live-trade-sell-${contract.contract_id}`}
            onClick={async () => {
              try {
                await send({
                  sell: contract.contract_id,
                  price: 0, // sell at market
                });
              } catch {
                /* ignore */
              }
            }}
            className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-vts-surface border border-vts-line text-vts-silver hover:bg-vts-loss/15 hover:border-vts-loss/40 hover:text-vts-loss transition"
          >
            Sell now @ {fmt(sellPrice)}
          </button>
        </div>
      )}
    </div>
  );
};

const LiveTradeFeed = ({
  contracts = [],
  currency = "USD",
  authorized,
  demoMode,
  onRefresh,
  onSettled,
}) => {
  const { session } = useAuth();
  // Aggregate live profit across all open contracts (updates every WS tick)
  const [liveProfits, setLiveProfits] = useState({}); // contract_id -> { profit, sold }
  const [reconciling, setReconciling] = useState(false);

  // v8.9.130 — Hide rows that have been stuck "open" for more than 24h.
  // Binary contracts on Deriv max out at 24h, so anything older is
  // definitely settled in Deriv's books — we just never received the
  // settlement WS frame. Showing them in Live Trades is pure noise.
  // We compute the cutoff via state-of-the-clock (refreshed every 60s)
  // so the filter is deterministic per render and purity-clean.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const cutoffMs = nowMs - 24 * 60 * 60 * 1000;
  const visibleContracts = contracts.filter((c) => {
    const stamp = c.date_start || c.purchase_time || c.entry_tick_time || c.created_at;
    if (!stamp) return true;
    const t = typeof stamp === "number"
      ? stamp * (stamp > 1e12 ? 1 : 1000)
      : Date.parse(stamp);
    return Number.isFinite(t) ? t >= cutoffMs : true;
  });
  const hiddenStaleCount = contracts.length - visibleContracts.length;

  // v8.9.130 — Manual "Reconcile" button. Calls the new backend endpoint
  // that force-settles every open trade_events row whose expiry is in
  // the past (sets outcome="expired", profit=0). Useful when a Demo
  // session has accumulated stuck rows from earlier WS drops.
  const handleReconcile = async () => {
    const acctId = session?.account_id;
    if (!acctId) return;
    setReconciling(true);
    try {
      const { data } = await api.post(
        `/track/trades/reconcile/${encodeURIComponent(acctId)}`,
      );
      const swept = data?.settled || 0;
      // Trigger parent refresh so the cleared rows drop out.
      onRefresh?.();
      // Tiny inline status — we don't import toast here to keep the
      // component lean; the row count drop is feedback enough.
      console.info(`[VTS] reconcile cleared ${swept} stuck trade${swept === 1 ? "" : "s"}`);
    } catch (e) {
      console.warn("[VTS] reconcile failed:", e?.message || e);
    } finally {
      setReconciling(false);
    }
  };

  // Stuck-OPEN reconciler — Feb 2026 user report: "trades still marked
  // open are not closing". The per-row WS subscription is the primary
  // settlement path, but it can miss the final `is_sold` frame on a
  // network blip. We belt-and-braces every 45s by asking the backend
  // (which uses the bot's auth) to ping Deriv for each open contract
  // and write the resolved state into BOTH bot_trades and trade_events.
  // Triggers onRefresh after a successful reconcile so the row drops
  // out of `contracts` and unmounts the subscription cleanly.
  useEffect(() => {
    if (!authorized || demoMode || !session?.account_id || !session?.token) return;
    let cancelled = false;
    const reconcile = async () => {
      try {
        const { data } = await api.post("/bot/reconcile", {
          account_id: session.account_id,
          access_token: session.token,
        });
        if (!cancelled && data?.updated > 0) {
          onRefresh?.(); // refresh the contracts list — settled rows drop out
        }
      } catch {
        /* silent — reconcile is best-effort */
      }
    };
    // First pass after a short delay so the WS subs land first.
    const initial = setTimeout(reconcile, 15_000);
    const id = setInterval(reconcile, 45_000);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(id);
    };
  }, [authorized, demoMode, session?.account_id, session?.token, onRefresh]);

  const handleLiveProfit = (cid, profit, sold) => {
    setLiveProfits((p) => ({ ...p, [cid]: { profit, sold } }));
  };

  const handleSettled = (cid, c) => {
    onSettled?.(cid, c);
  };

  // Total unrealised P&L = sum of profits on UNSOLD contracts only
  const totalUnrealised = Object.values(liveProfits).reduce(
    (a, b) => a + (b?.sold ? 0 : Number.isFinite(b?.profit) ? b.profit : 0),
    0
  );
  const aggregateColor =
    totalUnrealised > 0
      ? "text-vts-profit"
      : totalUnrealised < 0
      ? "text-vts-loss"
      : "text-vts-silver/70";

  // v8.9.123 — User mandate: "I wanna see all of the live trades showing
  // up in the live trades panel" (including Demo). Previously this panel
  // returned a "demo mode" placeholder when `demoMode=true`, which hid
  // every open Demo contract. The Deriv portfolio stream works identically
  // on Demo (D0T…/VRTC) and Real accounts, so we now render the same UI
  // and just rely on `authorized` for the connection guard.
  if (!authorized) {
    return (
      <div data-testid="live-trade-feed" className="vts-card overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-vts-line">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-vts-blue" />
            <div>
              <h3 className="font-heading font-semibold text-base text-white tracking-tight">
                Live trades
              </h3>
              <div className="text-xs text-vts-silver/60 mt-0.5 font-mono">
                Connect a Deriv account to stream live trades
              </div>
            </div>
          </div>
        </div>
        <div className="p-10 text-center">
          <Inbox className="h-8 w-8 mx-auto mb-3 text-vts-silver/30" />
          <div className="text-sm text-vts-silver/60">
            Not connected
          </div>
          <div className="text-xs mt-1 font-mono text-vts-silver/40">
            Tap &quot;Login with Deriv&quot; to see open contracts with live P&amp;L.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="live-trade-feed" className="vts-card overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-vts-line gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="h-4 w-4 text-vts-blue animate-pulse" />
          <div>
            <h3 className="font-heading font-semibold text-base text-white tracking-tight">
              Live trades
            </h3>
            <div className="text-xs text-vts-silver/60 mt-0.5 font-mono">
              {visibleContracts.length} open · streaming real-time P&L
              {hiddenStaleCount > 0 && (
                <span className="ml-2 text-yellow-300/70">
                  · {hiddenStaleCount} stale hidden (≥24h)
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/50 font-mono">
              Unrealised P&L
            </div>
            <div
              data-testid="live-trade-total-pnl"
              className={`font-mono text-lg tabular-nums font-medium ${aggregateColor}`}
            >
              {totalUnrealised > 0 ? "+" : ""}
              {fmt(totalUnrealised)} {currency}
            </div>
          </div>
          <Button
            data-testid="live-trade-reconcile-btn"
            variant="outline"
            size="sm"
            onClick={handleReconcile}
            disabled={reconciling || !session?.account_id}
            className="border-vts-line bg-vts-surface text-vts-silver hover:bg-vts-surfaceHover hover:text-white"
            title="Force-settle any contracts stuck 'open' past their expiry (binary contracts max at 24h, so anything older is definitely settled in Deriv's books)."
          >
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${reconciling ? "animate-spin" : ""}`} />
            {reconciling ? "Cleaning…" : "Reconcile"}
          </Button>
          <Button
            data-testid="live-trade-refresh-btn"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="border-vts-line bg-vts-surface text-vts-silver hover:bg-vts-surfaceHover hover:text-white"
          >
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {visibleContracts.length === 0 ? (
        <div className="p-12 text-center text-vts-silver/50">
          <Inbox className="h-8 w-8 mx-auto mb-3 text-vts-silver/30" />
          <div className="text-sm">No open positions</div>
          <div className="text-xs mt-1 font-mono">
            Place a trade or start the bot — it&apos;ll stream here live.
          </div>
          {hiddenStaleCount > 0 && (
            <div className="text-[10px] mt-3 text-yellow-300/70 font-mono">
              {hiddenStaleCount} stale row{hiddenStaleCount === 1 ? "" : "s"} hidden (≥24h old). Tap <strong>Reconcile</strong> to clear them permanently.
            </div>
          )}
        </div>
      ) : (
        <div className="divide-y divide-vts-line/30">
          {visibleContracts.map((c) => (
            <LiveContractRow
              key={c.contract_id}
              contract={c}
              currency={currency}
              onSettled={handleSettled}
              onLiveProfit={handleLiveProfit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveTradeFeed;
